import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Search, X } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { formatRupiah, formatNumber, statusClass } from "../lib/format";
import { Section, EmptyState, Skeleton } from "../components/UI";

const empty = { nama: "", kategori: "", harga: "", satuan: "kg", stok: "", status: "Aktif", deskripsi: "" };

export default function ProdukPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const [masterProducts, setMasterProducts] = useState([]);
  const [masterCategories, setMasterCategories] = useState([]);

  const loadMasterData = async () => {
    try {
      const [prodRes, catRes] = await Promise.all([
        api.get("/master-data/products"),
        api.get("/master-data/categories"),
      ]);
      setMasterProducts(prodRes.data);
      setMasterCategories(catRes.data);
    } catch (e) {
      console.error("Failed to load master data:", e);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products/mine");
      setRows(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); loadMasterData(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setShowForm(true); setError(""); };
  const openEdit = (r) => {
    setEditing(r); setForm({ ...r });
    setShowForm(true); setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const payload = {
      nama: form.nama,
      kategori: form.kategori,
      harga: Number(form.harga),
      satuan: form.satuan,
      stok: Number(form.stok),
      status: form.status,
      deskripsi: form.deskripsi || null,
    };
    try {
      if (editing) await api.patch(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      setShowForm(false);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Hapus produk "${r.nama}"?`)) return;
    await api.delete(`/products/${r.id}`);
    await load();
  };

  const filtered = rows.filter((r) =>
    !query ||
    r.nama.toLowerCase().includes(query.toLowerCase()) ||
    r.kategori.toLowerCase().includes(query.toLowerCase()),
  );

  return (
    <div className="space-y-6" data-testid="produk-page">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="kedan-label text-slate-500">Inventaris</div>
          <h1 className="text-3xl font-display font-bold text-slate-900 mt-1">Kelola Produk</h1>
          <p className="text-slate-500 text-sm mt-1">CRUD daftar komoditas, harga, dan stok Anda.</p>
        </div>
        <button onClick={openCreate} className="kedan-btn-primary" data-testid="add-product-btn">
          <Plus className="w-4 h-4" /> Tambah Produk
        </button>
      </header>

      <div className="kedan-card overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input className="kedan-input !pl-9" placeholder="Cari produk atau kategori..."
              value={query} onChange={(e) => setQuery(e.target.value)} data-testid="product-search" />
          </div>
          <div className="text-xs text-slate-500">{filtered.length} item</div>
        </div>
        {loading ? (
          <div className="p-6 space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            title="Belum ada produk"
            description="Tambahkan komoditas untuk mulai menerima Purchase Order dari SPPG."
          />
        ) : (
          <table className="kedan-table">
            <thead>
              <tr>
                <th>Nama Produk</th>
                <th>Kategori</th>
                <th className="text-right">Harga</th>
                <th>Satuan</th>
                <th className="text-right">Stok</th>
                <th>Status</th>
                <th className="text-right">Aksi</th>
              </tr>
            </thead>
            <tbody data-testid="product-table-body">
              {filtered.map((r) => (
                <tr key={r.id}>
                  <td className="font-medium text-slate-900">{r.nama}</td>
                  <td>{r.kategori}</td>
                  <td className="text-right font-mono">{formatRupiah(r.harga)}</td>
                  <td>{r.satuan}</td>
                  <td className="text-right font-mono">{formatNumber(r.stok)}</td>
                  <td><span className={`kedan-badge ${statusClass(r.status)}`}>{r.status}</span></td>
                  <td className="text-right">
                    <div className="inline-flex gap-2">
                      <button onClick={() => openEdit(r)} className="p-1.5 hover:bg-slate-100 rounded text-slate-600" data-testid={`edit-product-${r.id}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => remove(r)} className="p-1.5 hover:bg-red-50 rounded text-red-600" data-testid={`delete-product-${r.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="product-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h3 className="font-display font-semibold text-slate-900">{editing ? "Edit Produk" : "Tambah Produk"}</h3>
              <button onClick={() => setShowForm(false)} className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Nama Produk</label>
                  <select className="kedan-input" required value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} data-testid="form-product-nama">
                    <option value="">-- Pilih Nama Produk --</option>
                    {masterProducts.map((mp) => (
                      <option key={mp.id} value={mp.nama}>{mp.nama}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Kategori</label>
                  <select className="kedan-input" required value={form.kategori} onChange={(e) => setForm({ ...form, kategori: e.target.value })} data-testid="form-product-kategori">
                    <option value="">-- Pilih Kategori --</option>
                    {masterCategories.map((mc) => (
                      <option key={mc.id} value={mc.nama}>{mc.nama}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Satuan</label>
                  <select className="kedan-input" value={form.satuan} onChange={(e) => setForm({ ...form, satuan: e.target.value })} data-testid="form-product-satuan">
                    <option>kg</option><option>liter</option><option>pcs</option><option>karung</option><option>dus</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Harga (Rp)</label>
                  <input className="kedan-input" type="number" min="0" required value={form.harga} onChange={(e) => setForm({ ...form, harga: e.target.value })} data-testid="form-product-harga" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Stok</label>
                  <input className="kedan-input" type="number" min="0" required value={form.stok} onChange={(e) => setForm({ ...form, stok: e.target.value })} data-testid="form-product-stok" />
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Status</label>
                  <select className="kedan-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="form-product-status">
                    <option>Aktif</option><option>Nonaktif</option><option>Habis</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Deskripsi</label>
                  <textarea className="kedan-input" rows="2" value={form.deskripsi || ""} onChange={(e) => setForm({ ...form, deskripsi: e.target.value })} data-testid="form-product-deskripsi" />
                </div>
              </div>
              {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2">{error}</div>}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="kedan-btn-secondary">Batal</button>
                <button type="submit" className="kedan-btn-primary" data-testid="form-product-submit">{editing ? "Simpan Perubahan" : "Tambah Produk"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
